import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  ShieldCheck, Plus, Copy, ExternalLink, Loader2, CheckCircle2,
  Clock, AlertTriangle, Trash2, Banknote, Unlock, X,
  Link2, AlertOctagon, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { depositsApi, damageItemsApi } from '../services/api';

// Format YYYY-MM-DD → DD/MM/YYYY
const formatDateFR = (iso) => {
  if (!iso || typeof iso !== 'string') return iso || '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};

const EMPTY_FORM = {
  kind: 'single',
  mode: 'hold',
  label: '',
  guest_first_name: '',
  guest_last_name: '',
  guest_email: '',
  property_name: '',
  property_address: '',
  arrival_date: '',
  departure_date: '',
  amount_eur: '',
  notes: '',
};

const EMPTY_DAMAGE = { label: '', price_eur: '', category: '', active: true };

const StatusBadge = ({ status }) => {
  const map = {
    template: { label: 'Lien permanent', cls: 'bg-violet-100 text-violet-700 border-violet-200', icon: Link2 },
    awaiting_payment: { label: 'En attente paiement', cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
    authorized: { label: 'Empreinte CB OK', cls: 'bg-blue-100 text-blue-700 border-blue-200', icon: ShieldCheck },
    captured: { label: 'Capturée', cls: 'bg-purple-100 text-purple-700 border-purple-200', icon: Banknote },
    canceled: { label: 'Libérée', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
    expired: { label: 'Expirée', cls: 'bg-slate-100 text-slate-600 border-slate-200', icon: AlertTriangle },
    failed: { label: 'Échec', cls: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle },
  };
  const item = map[status] || { label: status, cls: 'bg-slate-100 text-slate-700', icon: Clock };
  const Icon = item.icon;
  return (
    <Badge className={`${item.cls} border`}>
      <Icon className="h-3 w-3 mr-1" /> {item.label}
    </Badge>
  );
};

export default function DepositsManagement() {
  const [tab, setTab] = useState('sessions'); // 'sessions' | 'templates' | 'damage'
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createdDeposit, setCreatedDeposit] = useState(null);
  const [filter, setFilter] = useState('all');

  // Damage items tab
  const [damageList, setDamageList] = useState([]);
  const [showDamageDialog, setShowDamageDialog] = useState(false);
  const [damageForm, setDamageForm] = useState(EMPTY_DAMAGE);
  const [editingDamageId, setEditingDamageId] = useState(null);
  const [savingDamage, setSavingDamage] = useState(false);

  // Capture dialog
  const [showCapture, setShowCapture] = useState(false);
  const [captureDeposit, setCaptureDeposit] = useState(null);
  const [damageItems, setDamageItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState({}); // {id: qty}
  const [captureReason, setCaptureReason] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [capturing, setCapturing] = useState(false);

  const fetchDeposits = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await depositsApi.list();
      setDeposits(data || []);
    } catch (e) {
      toast.error('Impossible de charger les cautions');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDamageList = useCallback(async () => {
    try {
      const { data } = await damageItemsApi.list();
      setDamageList(data || []);
    } catch (e) {
      toast.error('Impossible de charger la grille');
    }
  }, []);

  useEffect(() => { fetchDeposits(); fetchDamageList(); }, [fetchDeposits, fetchDamageList]);

  const openCreateDialog = (kind = 'single') => {
    setForm({ ...EMPTY_FORM, kind });
    setShowCreate(true);
  };

  const handleCreate = async () => {
    if (!form.amount_eur) {
      toast.error('Le montant est obligatoire');
      return;
    }
    if (form.kind === 'single') {
      if (!form.guest_first_name || !form.guest_last_name || !form.property_name || !form.arrival_date || !form.departure_date) {
        toast.error('Champs obligatoires manquants');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        kind: form.kind,
        mode: form.mode || 'hold',
        amount_eur: parseFloat(form.amount_eur),
        label: form.label || null,
        notes: form.notes || null,
      };
      if (form.kind === 'single') {
        Object.assign(payload, {
          guest_first_name: form.guest_first_name,
          guest_last_name: form.guest_last_name,
          guest_email: form.guest_email || null,
          property_name: form.property_name,
          property_address: form.property_address,
          arrival_date: form.arrival_date,
          departure_date: form.departure_date,
        });
      } else {
        // Optional pre-fill on template (property only)
        if (form.property_name) payload.property_name = form.property_name;
        if (form.property_address) payload.property_address = form.property_address;
      }
      const { data } = await depositsApi.create(payload);
      toast.success(form.kind === 'template' ? 'Lien permanent créé.' : 'Caution créée. Lien prêt à envoyer.');
      setForm(EMPTY_FORM);
      setShowCreate(false);
      setCreatedDeposit(data);
      fetchDeposits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const publicLink = (token) => `${window.location.origin}/caution/${token}`;

  const copyLink = (token) => {
    navigator.clipboard.writeText(publicLink(token));
    toast.success('Lien copié dans le presse-papier');
  };

  const openCapture = async (deposit) => {
    setCaptureDeposit(deposit);
    setSelectedItems({});
    setCaptureReason('');
    setCustomAmount('');
    setShowCapture(true);
    try {
      const { data } = await damageItemsApi.list();
      setDamageItems((data || []).filter(d => d.active));
    } catch (e) {
      setDamageItems([]);
    }
  };

  const toggleItem = (it) => {
    setSelectedItems((s) => {
      const next = { ...s };
      if (next[it.id]) { delete next[it.id]; } else { next[it.id] = 1; }
      return next;
    });
  };

  const setQty = (id, qty) => {
    const q = Math.max(0, parseInt(qty || 0, 10));
    setSelectedItems((s) => {
      const next = { ...s };
      if (q <= 0) delete next[id]; else next[id] = q;
      return next;
    });
  };

  const sumItems = damageItems
    .filter(d => selectedItems[d.id])
    .reduce((acc, d) => acc + d.price_eur * selectedItems[d.id], 0);

  const finalAmount = customAmount ? parseFloat(customAmount) : sumItems;

  const handleCapture = async () => {
    if (!captureDeposit) return;
    if (!finalAmount || finalAmount <= 0) {
      toast.error('Sélectionnez des items ou indiquez un montant');
      return;
    }
    if (finalAmount > (captureDeposit.authorized_amount || captureDeposit.amount_eur) + 0.001) {
      toast.error('Montant supérieur au montant autorisé');
      return;
    }
    setCapturing(true);
    try {
      const items = damageItems
        .filter(d => selectedItems[d.id])
        .map(d => ({ item_id: d.id, label: d.label, price_eur: d.price_eur, qty: selectedItems[d.id] }));
      await depositsApi.capture(captureDeposit.id, {
        items,
        amount_eur: customAmount ? parseFloat(customAmount) : undefined,
        reason: captureReason || null,
      });
      toast.success('Caution capturée');
      setShowCapture(false);
      fetchDeposits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de capture');
    } finally {
      setCapturing(false);
    }
  };

  const handleRelease = async (deposit) => {
    if (!window.confirm(`Libérer l'empreinte CB de ${deposit.guest_first_name} ${deposit.guest_last_name} (${deposit.amount_eur}€) ?`)) return;
    try {
      await depositsApi.release(deposit.id);
      toast.success('Caution libérée');
      fetchDeposits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la libération');
    }
  };

  const handleDelete = async (deposit) => {
    if (!window.confirm(deposit.kind === 'template' ? 'Supprimer ce lien permanent ?' : 'Supprimer définitivement cette caution ?')) return;
    try {
      await depositsApi.delete(deposit.id);
      toast.success('Supprimé');
      fetchDeposits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  // Damage item handlers
  const openDamageDialog = (item) => {
    if (item) {
      setDamageForm({
        label: item.label,
        price_eur: String(item.price_eur),
        category: item.category || '',
        active: item.active,
      });
      setEditingDamageId(item.id);
    } else {
      setDamageForm(EMPTY_DAMAGE);
      setEditingDamageId(null);
    }
    setShowDamageDialog(true);
  };

  const handleSaveDamage = async () => {
    if (!damageForm.label.trim() || damageForm.price_eur === '') {
      toast.error('Libellé et prix obligatoires');
      return;
    }
    setSavingDamage(true);
    try {
      const payload = {
        label: damageForm.label.trim(),
        price_eur: parseFloat(damageForm.price_eur),
        category: damageForm.category.trim() || null,
        active: damageForm.active,
      };
      if (editingDamageId) {
        await damageItemsApi.update(editingDamageId, payload);
        toast.success('Item mis à jour');
      } else {
        await damageItemsApi.create(payload);
        toast.success('Item créé');
      }
      setShowDamageDialog(false);
      fetchDamageList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSavingDamage(false);
    }
  };

  const handleDeleteDamage = async (id) => {
    if (!window.confirm('Supprimer cet item ?')) return;
    try {
      await damageItemsApi.delete(id);
      toast.success('Supprimé');
      fetchDamageList();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  // Split deposits by kind
  const sessions = deposits.filter(d => d.status !== 'template');
  const templates = deposits.filter(d => d.status === 'template');
  const filteredSessions = filter === 'all' ? sessions : sessions.filter(d => d.status === filter);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 p-4 lg:p-8 lg:ml-64">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Cautions voyageurs</h1>
                <p className="text-sm text-slate-500">Empreintes CB Stripe, capture jusqu'à 14 jours</p>
              </div>
            </div>
            {tab === 'sessions' && (
              <Button onClick={() => openCreateDialog('single')} className="bg-blue-600 hover:bg-blue-700" data-testid="create-deposit-btn">
                <Plus className="h-4 w-4 mr-2" /> Nouvelle caution
              </Button>
            )}
            {tab === 'templates' && (
              <Button onClick={() => openCreateDialog('template')} className="bg-violet-600 hover:bg-violet-700" data-testid="create-template-btn">
                <Plus className="h-4 w-4 mr-2" /> Nouveau lien permanent
              </Button>
            )}
            {tab === 'damage' && (
              <Button onClick={() => openDamageDialog(null)} className="bg-rose-600 hover:bg-rose-700" data-testid="create-damage-btn">
                <Plus className="h-4 w-4 mr-2" /> Nouvel item
              </Button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-5 bg-slate-100 p-1 rounded-xl w-fit">
            {[
              { k: 'sessions', label: 'Cautions', icon: ShieldCheck, count: sessions.length },
              { k: 'templates', label: 'Liens permanents', icon: Link2, count: templates.length },
              { k: 'damage', label: 'Grille dégâts', icon: AlertOctagon, count: damageList.length },
            ].map(t => {
              const Icon = t.icon;
              return (
                <button
                  key={t.k}
                  onClick={() => setTab(t.k)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    tab === t.k
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid={`tab-${t.k}`}
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${tab === t.k ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600'}`}>{t.count}</span>
                </button>
              );
            })}
          </div>

          {/* Filters (only for sessions tab) */}
          {tab === 'sessions' && (
            <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
              {[
                { k: 'all', label: 'Toutes' },
                { k: 'awaiting_payment', label: 'En attente' },
                { k: 'authorized', label: 'Empreintes OK' },
                { k: 'captured', label: 'Capturées' },
                { k: 'canceled', label: 'Libérées' },
              ].map(f => (
                <button
                  key={f.k}
                  onClick={() => setFilter(f.k)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    filter === f.k
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-white text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`filter-${f.k}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}

          {/* CONTENT — SESSIONS TAB */}
          {tab === 'sessions' && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : filteredSessions.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <p className="font-medium">Aucune caution {filter !== 'all' ? 'dans cet état' : ''}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Voyageur</TableHead>
                      <TableHead>Logement</TableHead>
                      <TableHead>Séjour</TableHead>
                      <TableHead className="text-right">Montant</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSessions.map((d) => (
                      <TableRow key={d.id} data-testid={`deposit-row-${d.id}`}>
                        <TableCell>
                          <div className="font-medium">{d.guest_first_name} {d.guest_last_name}</div>
                          {d.card_last4 && <div className="text-xs text-slate-500">{d.card_brand?.toUpperCase()} •••• {d.card_last4}</div>}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">{d.property_name}</div>
                          <div className="text-xs text-slate-500 truncate max-w-xs">{d.property_address}</div>
                        </TableCell>
                        <TableCell className="text-sm">{formatDateFR(d.arrival_date)} → {formatDateFR(d.departure_date)}</TableCell>
                        <TableCell className="text-right font-bold">
                          {d.amount_eur} €
                          {d.captured_amount > 0 && (
                            <div className="text-xs text-purple-600 font-normal">Capturé : {d.captured_amount} €</div>
                          )}
                        </TableCell>
                        <TableCell><StatusBadge status={d.status} /></TableCell>
                        <TableCell className="text-right space-x-1">
                          {d.status === 'awaiting_payment' && (
                            <>
                              <Button variant="ghost" size="icon" title="Copier le lien" onClick={() => copyLink(d.token)} data-testid={`copy-link-${d.id}`}>
                                <Copy className="h-4 w-4 text-blue-600" />
                              </Button>
                              <Button variant="ghost" size="icon" title="Ouvrir le lien" onClick={() => window.open(publicLink(d.token), '_blank')}>
                                <ExternalLink className="h-4 w-4 text-blue-600" />
                              </Button>
                              <Button variant="ghost" size="icon" title="Supprimer" onClick={() => handleDelete(d)}>
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </>
                          )}
                          {d.status === 'authorized' && (
                            <>
                              <Button variant="ghost" size="icon" title="Capturer (dégâts)" onClick={() => openCapture(d)} data-testid={`capture-${d.id}`}>
                                <Banknote className="h-4 w-4 text-purple-600" />
                              </Button>
                              <Button variant="ghost" size="icon" title="Libérer l'empreinte" onClick={() => handleRelease(d)} data-testid={`release-${d.id}`}>
                                <Unlock className="h-4 w-4 text-emerald-600" />
                              </Button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          )}

          {/* CONTENT — TEMPLATES TAB */}
          {tab === 'templates' && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
                  </div>
                ) : templates.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Link2 className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                    <p className="font-medium mb-1">Aucun lien permanent</p>
                    <p className="text-sm">Créez un lien réutilisable que vous enverrez automatiquement aux voyageurs Booking/Airbnb. Ils rempliront leurs infos eux-mêmes.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Libellé</TableHead>
                        <TableHead>Logement pré-rempli</TableHead>
                        <TableHead className="text-right">Montant</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {templates.map((t) => (
                        <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                          <TableCell>
                            <div className="font-medium flex items-center gap-2">
                              {t.label || `Caution ${t.amount_eur}€`}
                              {t.mode === 'auth' ? (
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">VÉRIF 1€</span>
                              ) : (
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">EMPREINTE</span>
                              )}
                            </div>
                            <div className="text-xs text-slate-400 font-mono break-all">{t.token.slice(0, 12)}…</div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {t.property_name ? (
                              <>
                                <div>{t.property_name}</div>
                                <div className="text-xs text-slate-500">{t.property_address}</div>
                              </>
                            ) : <span className="text-slate-400 italic">À remplir par le voyageur</span>}
                          </TableCell>
                          <TableCell className="text-right font-bold text-violet-700">{t.amount_eur} €</TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button variant="ghost" size="icon" title="Copier le lien" onClick={() => copyLink(t.token)} data-testid={`copy-template-${t.id}`}>
                              <Copy className="h-4 w-4 text-violet-600" />
                            </Button>
                            <Button variant="ghost" size="icon" title="Ouvrir le lien" onClick={() => window.open(publicLink(t.token), '_blank')}>
                              <ExternalLink className="h-4 w-4 text-violet-600" />
                            </Button>
                            <Button variant="ghost" size="icon" title="Supprimer" onClick={() => handleDelete(t)}>
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}

          {/* CONTENT — DAMAGE GRID TAB */}
          {tab === 'damage' && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                {damageList.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <AlertOctagon className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                    <p className="font-medium mb-1">Grille vide</p>
                    <p className="text-sm">Ajoutez les tarifs de dégâts (fumée, animaux, casse…) appliqués lors d'une capture de caution.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Libellé</TableHead>
                        <TableHead>Catégorie</TableHead>
                        <TableHead className="text-right">Prix</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {damageList.map((it) => (
                        <TableRow key={it.id} data-testid={`damage-row-${it.id}`}>
                          <TableCell className="font-medium">{it.label}</TableCell>
                          <TableCell className="text-slate-500">{it.category || '·'}</TableCell>
                          <TableCell className="text-right font-bold">{it.price_eur.toFixed(2)} €</TableCell>
                          <TableCell>
                            {it.active
                              ? <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Actif</span>
                              : <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-medium">Inactif</span>}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" onClick={() => openDamageDialog(it)} data-testid={`edit-damage-${it.id}`}>
                              <Pencil className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteDamage(it.id)} data-testid={`delete-damage-${it.id}`}>
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create dialog (single OR template) */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {form.kind === 'template' ? 'Nouveau lien permanent' : 'Nouvelle caution voyageur'}
            </DialogTitle>
            <DialogDescription>
              {form.kind === 'template'
                ? 'Génère un lien réutilisable. Le voyageur remplira ses propres infos (Booking, Airbnb…).'
                : 'Remplissez les infos puis copiez le lien à envoyer au voyageur.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {/* Mode selector — always visible */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Mode de caution</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, mode: 'hold' })}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    form.mode === 'hold'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                  data-testid="mode-hold-btn"
                >
                  <div className="text-sm font-bold text-slate-900">🔒 Empreinte CB</div>
                  <div className="text-xs text-slate-600 mt-1">Réserve le montant total sur la carte. <strong>Sécurité maximale</strong>.</div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, mode: 'auth' })}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    form.mode === 'auth'
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                  data-testid="mode-auth-btn"
                >
                  <div className="text-sm font-bold text-slate-900">✨ Vérif 1€</div>
                  <div className="text-xs text-slate-600 mt-1">1€ remboursé + carte sauvegardée. <strong>Conversion maximale</strong>.</div>
                </button>
              </div>
              {form.mode === 'auth' && (
                <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                  ℹ️ En cas de dégât, vous débitez jusqu'au montant max indiqué. Le succès dépend de la banque du voyageur (~85% succès). Voyageur prévenu dans les CGV.
                </p>
              )}
              {form.mode === 'hold' && (
                <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg p-2">
                  ℹ️ Le montant total est bloqué sur la carte pendant 7 jours. Garantie 100% de pouvoir prélever.
                </p>
              )}
            </div>

            {form.kind === 'template' && (
              <div className="space-y-1">
                <Label>Libellé du lien (optionnel)</Label>
                <Input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="Ex: Caution Villa Bretonne — 500€"
                  data-testid="form-label"
                />
                <p className="text-xs text-slate-500">Sert uniquement à vous repérer en interne.</p>
              </div>
            )}

            {form.kind === 'single' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Prénom *</Label>
                    <Input value={form.guest_first_name} onChange={(e) => setForm({ ...form, guest_first_name: e.target.value })} data-testid="form-firstname" />
                  </div>
                  <div className="space-y-1">
                    <Label>Nom *</Label>
                    <Input value={form.guest_last_name} onChange={(e) => setForm({ ...form, guest_last_name: e.target.value })} data-testid="form-lastname" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Email (optionnel)</Label>
                  <Input type="email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} />
                </div>
              </>
            )}
            <div className="space-y-1">
              <Label>{form.kind === 'template' ? 'Nom du logement (optionnel)' : 'Nom du logement *'}</Label>
              <Input value={form.property_name} onChange={(e) => setForm({ ...form, property_name: e.target.value })} placeholder="Ex: Villa Bretonne" data-testid="form-property-name" />
              {form.kind === 'template' && <p className="text-xs text-slate-500">Laissez vide si vous voulez que le voyageur le précise lui-même.</p>}
            </div>
            <div className="space-y-1">
              <Label>{form.kind === 'template' ? 'Adresse du logement (optionnel)' : 'Adresse du logement *'}</Label>
              <Input value={form.property_address} onChange={(e) => setForm({ ...form, property_address: e.target.value })} placeholder="12 rue de la Plage, 22240 Plouha" data-testid="form-property-address" />
            </div>
            {form.kind === 'single' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Arrivée *</Label>
                  <Input type="date" value={form.arrival_date} onChange={(e) => setForm({ ...form, arrival_date: e.target.value })} data-testid="form-arrival" />
                </div>
                <div className="space-y-1">
                  <Label>Départ *</Label>
                  <Input type="date" value={form.departure_date} onChange={(e) => setForm({ ...form, departure_date: e.target.value })} data-testid="form-departure" />
                </div>
              </div>
            )}
            <div className="space-y-1">
              <Label>Montant de la caution (€) *</Label>
              <Input type="number" step="0.01" value={form.amount_eur} onChange={(e) => setForm({ ...form, amount_eur: e.target.value })} placeholder="500" data-testid="form-amount" />
            </div>
            {form.kind === 'single' && (
              <div className="space-y-1">
                <Label>Notes internes (optionnel)</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={saving} className={form.kind === 'template' ? 'bg-violet-600 hover:bg-violet-700' : 'bg-blue-600 hover:bg-blue-700'} data-testid="submit-deposit-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (form.kind === 'template' ? 'Créer le lien permanent' : 'Créer la caution')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link created dialog */}
      <Dialog open={!!createdDeposit} onOpenChange={(o) => !o && setCreatedDeposit(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {createdDeposit?.kind === 'template' ? 'Lien permanent prêt' : 'Lien de caution prêt'}
            </DialogTitle>
            <DialogDescription>
              {createdDeposit?.kind === 'template'
                ? 'Lien réutilisable. Configurez-le dans vos messages automatiques Booking / Airbnb.'
                : 'Copiez ce lien et envoyez-le au voyageur (SMS, WhatsApp, mail…).'}
            </DialogDescription>
          </DialogHeader>
          {createdDeposit && (
            <div className="space-y-3">
              <div className={`${createdDeposit.kind === 'template' ? 'bg-violet-50 border-violet-200' : 'bg-blue-50 border-blue-200'} border rounded-xl p-3`}>
                <p className={`text-xs mb-1 font-medium ${createdDeposit.kind === 'template' ? 'text-violet-700' : 'text-blue-700'}`}>URL publique</p>
                <p className={`text-sm font-mono break-all ${createdDeposit.kind === 'template' ? 'text-violet-900' : 'text-blue-900'}`}>{publicLink(createdDeposit.token)}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-slate-50 p-2 rounded">
                  <span className="text-slate-500">{createdDeposit.kind === 'template' ? 'Libellé' : 'Voyageur'}</span>
                  <p className="font-medium">
                    {createdDeposit.kind === 'template'
                      ? (createdDeposit.label || `Caution ${createdDeposit.amount_eur}€`)
                      : `${createdDeposit.guest_first_name} ${createdDeposit.guest_last_name}`}
                  </p>
                </div>
                <div className="bg-slate-50 p-2 rounded">
                  <span className="text-slate-500">Montant</span>
                  <p className="font-medium">{createdDeposit.amount_eur} €</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={() => copyLink(createdDeposit.token)} data-testid="copy-created-link">
                  <Copy className="h-4 w-4 mr-2" /> Copier le lien
                </Button>
                <Button variant="outline" onClick={() => window.open(publicLink(createdDeposit.token), '_blank')}>
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreatedDeposit(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Capture dialog */}
      <Dialog open={showCapture} onOpenChange={setShowCapture}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Capturer une partie de la caution</DialogTitle>
            <DialogDescription>
              Cochez les éléments endommagés. Le montant capturé sera prélevé sur la CB du voyageur ; le reste est libéré automatiquement.
            </DialogDescription>
          </DialogHeader>
          {captureDeposit && (
            <div className="space-y-3 py-2">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm">
                <p><strong>{captureDeposit.guest_first_name} {captureDeposit.guest_last_name}</strong> · {captureDeposit.property_name}</p>
                <p className="text-blue-700">Empreinte autorisée : <strong>{captureDeposit.authorized_amount || captureDeposit.amount_eur} €</strong></p>
              </div>

              {damageItems.length === 0 ? (
                <div className="text-center py-4 text-slate-500 text-sm">
                  Aucun item dans la grille. <a href="/admin/grille-degats" className="text-blue-600 underline">En ajouter</a>
                </div>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {damageItems.map(it => {
                    const checked = !!selectedItems[it.id];
                    return (
                      <div key={it.id} className={`flex items-center gap-3 p-2 rounded-lg border ${checked ? 'border-purple-300 bg-purple-50' : 'border-slate-200'}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleItem(it)} className="rounded" data-testid={`select-item-${it.id}`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{it.label}</p>
                          {it.category && <p className="text-[10px] text-slate-400 uppercase">{it.category}</p>}
                        </div>
                        <span className="text-sm font-semibold">{it.price_eur} €</span>
                        {checked && (
                          <Input
                            type="number"
                            min="1"
                            value={selectedItems[it.id]}
                            onChange={(e) => setQty(it.id, e.target.value)}
                            className="w-16 h-8 text-sm"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="border-t pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Total grille</span>
                  <span className="font-bold text-purple-700">{sumItems.toFixed(2)} €</span>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Forcer un montant personnalisé (€) · optionnel</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder={`Max ${captureDeposit.authorized_amount || captureDeposit.amount_eur}€`}
                    data-testid="capture-custom-amount"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Motif (optionnel)</Label>
                  <Textarea value={captureReason} onChange={(e) => setCaptureReason(e.target.value)} rows={2} />
                </div>
                <div className="flex items-center justify-between bg-purple-50 border border-purple-200 p-3 rounded-lg">
                  <span className="font-bold">Montant à capturer</span>
                  <span className="text-xl font-bold text-purple-700">{finalAmount.toFixed(2)} €</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCapture(false)}>Annuler</Button>
            <Button onClick={handleCapture} disabled={capturing || finalAmount <= 0} className="bg-purple-600 hover:bg-purple-700" data-testid="confirm-capture-btn">
              {capturing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Banknote className="h-4 w-4 mr-2" /> Capturer {finalAmount.toFixed(2)} €</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Damage item create/edit dialog */}
      <Dialog open={showDamageDialog} onOpenChange={setShowDamageDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingDamageId ? 'Modifier l\'item' : 'Nouvel item'}</DialogTitle>
            <DialogDescription>Tarif appliqué lors de la capture d'une caution.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Libellé *</Label>
              <Input
                value={damageForm.label}
                onChange={(e) => setDamageForm({ ...damageForm, label: e.target.value })}
                placeholder="Ex: Fumée intérieure, Animal non déclaré…"
                data-testid="damage-label-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Prix (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={damageForm.price_eur}
                  onChange={(e) => setDamageForm({ ...damageForm, price_eur: e.target.value })}
                  placeholder="150"
                  data-testid="damage-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Input
                  value={damageForm.category}
                  onChange={(e) => setDamageForm({ ...damageForm, category: e.target.value })}
                  placeholder="Hygiène, Mobilier…"
                  data-testid="damage-category-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="damage-active-cbx"
                checked={damageForm.active}
                onChange={(e) => setDamageForm({ ...damageForm, active: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="damage-active-cbx" className="cursor-pointer">Actif (visible lors d'une capture)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDamageDialog(false)}>Annuler</Button>
            <Button onClick={handleSaveDamage} disabled={savingDamage} className="bg-rose-600 hover:bg-rose-700" data-testid="save-damage-btn">
              {savingDamage ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
